import {
  cloneElement,
  isValidElement,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { cx } from '../cx';
import { useEscapeKey, useOnClickOutside } from '../hooks';
import styles from './DropdownMenu.module.css';

export interface DropdownMenuItem {
  value: string;
  label: ReactNode;
  icon?: ReactNode;
  danger?: boolean;
  disabled?: boolean;
  /** Present => the item is a choice (`menuitemradio`) with this checked state, shown with a check mark. */
  checked?: boolean;
}

export interface DropdownMenuProps {
  /** A single focusable element (usually a Button or IconButton). Its own `ref`, if any, is replaced by this component's — pass a fresh element. */
  trigger: ReactElement<Record<string, unknown>>;
  items: DropdownMenuItem[];
  onSelect: (value: string) => void;
  align?: 'start' | 'end';
  'aria-label': string;
  className?: string;
}

/**
 * A button that opens a `role="menu"` panel with roving tabindex keyboard
 * navigation (Arrow Up/Down, Home/End), closes on Escape or an outside
 * click, and restores focus to the trigger on close. Not built on a native
 * `<select>` because a menu's items trigger arbitrary actions (assign,
 * escalate, ...) rather than picking a form value.
 */
export function DropdownMenu({ trigger, items, onSelect, align = 'start', 'aria-label': ariaLabel, className }: DropdownMenuProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const itemRefs = useRef<Record<number, HTMLButtonElement | null>>({});
  const menuId = useId();

  const enabledIndexes = items.reduce<number[]>((acc, item, i) => {
    if (!item.disabled) acc.push(i);
    return acc;
  }, []);

  function close(): void {
    setOpen(false);
    triggerRef.current?.focus();
  }

  useOnClickOutside(wrapperRef, () => setOpen(false), open);
  useEscapeKey(close, open);

  function openMenu(): void {
    const first = enabledIndexes[0] ?? 0;
    setActiveIndex(first);
    setOpen(true);
    requestAnimationFrame(() => itemRefs.current[first]?.focus());
  }

  function moveTo(index: number | undefined): void {
    if (index === undefined) return;
    setActiveIndex(index);
    itemRefs.current[index]?.focus();
  }

  function onMenuKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    const pos = enabledIndexes.indexOf(activeIndex);
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        moveTo(enabledIndexes[(pos + 1) % enabledIndexes.length]);
        break;
      case 'ArrowUp':
        e.preventDefault();
        moveTo(enabledIndexes[(pos - 1 + enabledIndexes.length) % enabledIndexes.length]);
        break;
      case 'Home':
        e.preventDefault();
        moveTo(enabledIndexes[0]);
        break;
      case 'End':
        e.preventDefault();
        moveTo(enabledIndexes[enabledIndexes.length - 1]);
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
        break;
    }
  }

  function select(item: DropdownMenuItem): void {
    if (item.disabled) return;
    onSelect(item.value);
    close();
  }

  if (!isValidElement(trigger)) return null;
  const triggerProps = trigger.props as Record<string, unknown> & { onClick?: (e: MouseEvent) => void };

  const clonedTrigger = cloneElement(trigger, {
    ref: (el: HTMLElement | null) => {
      triggerRef.current = el;
    },
    'aria-haspopup': 'menu',
    'aria-expanded': open,
    'aria-controls': open ? menuId : undefined,
    onClick: (e: MouseEvent) => {
      triggerProps.onClick?.(e);
      if (open) close();
      else openMenu();
    },
  });

  return (
    <div ref={wrapperRef} className={cx(styles.wrapper, className)}>
      {clonedTrigger}
      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label={ariaLabel}
          className={cx(styles.menu, styles[align])}
          onKeyDown={onMenuKeyDown}
        >
          {items.map((item, i) => (
            <button
              key={item.value}
              ref={(el) => {
                itemRefs.current[i] = el;
              }}
              type="button"
              role={item.checked === undefined ? 'menuitem' : 'menuitemradio'}
              {...(item.checked === undefined ? {} : { 'aria-checked': item.checked })}
              tabIndex={i === activeIndex ? 0 : -1}
              disabled={item.disabled}
              className={cx(styles.item, item.danger && styles.danger)}
              onFocus={() => setActiveIndex(i)}
              onClick={() => select(item)}
            >
              {item.icon && <span className={styles.icon}>{item.icon}</span>}
              {item.label}
              {item.checked && (
                <svg className={styles.check} width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
